import { BetQLSportItem } from "../bet-config";

/** Partial interface a a `game`, only providing the team stats necessary for getting a normal team record */
export interface TeamForDeterminingRecord {
  teamStats?: {
    wins?: number | null;
    losses?: number | null;
    suties?: number | null;
    overtimeLosses?: number | null;
  };
}

/** Indicates a team win, loss or draw */
type TeamRecordMetric = number | string | null;
export interface SimpleTeamFormatArgs {
  wins: TeamRecordMetric;
  losses: TeamRecordMetric;
  draws: TeamRecordMetric;
  overtimeLosses?: TeamRecordMetric;
  hasDraws?: boolean;
  league?: string | null;
}

export const getTeamRank = (team: any, league: BetQLSportItem): string => {
  const rank = team?.teamStats?.rank;

  if (!rank) {
    return "";
  }

  if (!["CFB", "CBK", "ATP", "WTA"].includes(league.key)) {
    return "";
  }

  if (["CFB", "CBK"].includes(league.key) && rank > 25) {
    return "";
  }

  return rank;
};

/**
 * Formats a team record outside of the context of a team, used a utility helper to determine record with primitive values.
 *
 * This can be utilized by other record getting functions by just passing their individual metrics to this utility
 */
export const simpleTeamRecordFormat = ({
  wins,
  losses,
  draws,
  hasDraws,
  league,
  overtimeLosses = null,
}: SimpleTeamFormatArgs): string => {
  const isNHL = league && league === "NHL";

  if (wins === null || losses === null) {
    return hasDraws || isNHL ? "-- -- --" : "-- --";
  }
  if (!hasDraws && !isNHL) {
    // Adding draws here accounts for the leagues that have ties, but now configured to have draws
    return `${wins}-${losses}${draws ? `-${draws}` : ""}`;
  }

  if (isNHL) {
    // NHL has overtime losses and we always want to show that instead
    // and overtimeLosses will be 0 if there is none by provider
    return `${wins}-${losses}-${overtimeLosses !== null ? overtimeLosses : "0"}`;
  }

  const drawsDisplay: string = draws !== null ? `-${draws}-` : " -- ";
  return wins + drawsDisplay + losses;
};

export const getTeamRecord = (team: TeamForDeterminingRecord, league: BetQLSportItem | null): string => {
  const { wins = null, losses = null, suties: draws = null, overtimeLosses = null } = team?.teamStats || {};
  const { hasDraws = false, key = null } = league || {};

  return simpleTeamRecordFormat({ wins, losses, draws, overtimeLosses, hasDraws, league: key });
};

export const getTeamAtsRecord = (team: any) => {
  const { wins = null, atswins = null, atslosses = null, atsties = null } = team?.teamStats || {};

  if (wins === null) {
    return "-- --";
  }

  return `${atswins || 0}-${atslosses || 0}${!atsties ? "" : `-${atsties}`}`; // prettier-ignore
};

export const getTeamOuRecord = (team: any) => {
  const { wins = null, overs = null, unders = null, pushes = null } = team?.teamStats || {};

  if (wins === null) {
    return "-- --";
  }

  return `${overs || 0}-${unders || 0}${!pushes ? "" : `-${pushes}`}`; // prettier-ignore
};

export const getTeamConfRecord = (team: any, league: BetQLSportItem): string => {
  const stats = team?.teamStats;
  let record = "";
  if (league.key === "CBK") {
    record += `${stats?.confWins || 0}-${stats?.confLosses || 0} Conf`;
  }
  return record;
};

export const getTeamPitcher = (pitcher: any, league: BetQLSportItem): string => {
  if (league.key !== "MLB") {
    return "";
  }

  if (!pitcher) {
    return "Pitchers not announced.";
  }

  const firstInitial = pitcher?.firstName?.substring(0, 1);
  const lastName = pitcher?.lastName;

  return `SP ${firstInitial}. ${lastName}`;
};

export const getTeamPitcherRecord = (pitcher: any, league: BetQLSportItem): string => {
  if (league.key !== "MLB") {
    return "";
  }

  if (!pitcher) {
    return "";
  }

  const winLoss = `${pitcher?.wins || 0}-${pitcher?.losses || 0}`;
  const era = `${pitcher?.earnedRunAvg || "--"} ERA`;

  return `${winLoss}, ${era}`;
};
